import PropTypes from "prop-types";
import React, { useState } from "react";
import {
  setBodyOverflow,
  turnOffPullToRefresh,
  turnOnPullToRefresh,
} from "../helpers/modal";
import { preventLinkDefault } from "../helpers/links";
import ExpensesList from "./ExpensesList";
import ExpensesMonthChart from "./ExpensesMonthChart";
import ExpensesYearChart from "./ExpensesYearChart";
import ExpectedExpensesChart from "./ExpectedExpensesChart";
import ExpensesMonthSwitcher from "./ExpensesMonthSwitcher";
import AiModal from "./AiModal";
import OpenAiModalButton from "./OpenAiModalButton";

const TOP_ICON_BUTTON_CSS =
  "mb-1 bg-blue-700 dark:bg-blue-600 dark:focus:ring-blue-800 dark:hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300 font-medium hover:bg-blue-800 inline-flex items-center px-3 py-2 rounded-lg text-center text-sm text-white";

const Expenses = ({
  categories = [],
  supportedCurrencies = [],
  expensesList,
}) => {
  const {
    name: expensesListName,
    kind: expensesListKind,
    kindLabel: expensesListKindLabel,
    currency: expensesListCurrency,
    totalBalance: expensesListTotalBalance,
    expensesPath,
    aiPath,
    editExpensesListPath,
    editExpectedExpensesPath,
    createExpectedExpensesPath,
    chartDataPath,
  } = expensesList;
  const [totalBalance, setTotalBalance] = useState(expensesListTotalBalance);
  const [showMonthSwitcher, setMonthSwitcher] = useState(false);
  const [showMonthChart, setShowMonthChart] = useState(false);
  const [showYearChart, setShowYearChart] = useState(false);
  const [showExpectedExpensesChart, setShowExpectedExpensesChart] =
    useState(false);
  const useMonth = expensesListKind === "monthly";
  const [monthValue, setMonthValue] = useState(
    `${new Date().getFullYear()}-${new Date().getMonth() + 1}`
  );
  const [showAiModal, setShowAiModal] = useState(false);

  const handleBalanceUpdate = (newBalance) => {
    setTotalBalance(newBalance);
  };

  const openCloseMonthChart = (event) => {
    preventLinkDefault(event);

    setShowMonthChart(!showMonthChart);
  };

  const openCloseYearChart = (event) => {
    preventLinkDefault(event);
    setShowYearChart(!showYearChart);
  };

  const openCloseExpectedExpensesChart = (event) => {
    preventLinkDefault(event);
    setShowExpectedExpensesChart(!showExpectedExpensesChart);
  };

  const openAiModal = (event) => {
    preventLinkDefault(event);

    turnOffPullToRefresh();
    setBodyOverflow(true);
    setShowAiModal(true);
  };

  const closeAiModal = () => {
    turnOnPullToRefresh();
    setBodyOverflow(false);
    setShowAiModal(false);
  };

  const toggleMonthSwitcher = (event) => {
    preventLinkDefault(event);
    setMonthSwitcher(!showMonthSwitcher);
  };

  return (
    <>
      <div
        className="px-2 flex flex-col items-center justify-center space-y-4"
        id="expenses_info"
      >
        <div className="relative bg-white dark:bg-gray-800 max-w-xl p-6 rounded-lg w-full card-shadow">
          <h5 className="flex items-center mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            {expensesListName}
            <a href={editExpensesListPath} className="ml-2">
              <svg
                className="w-6 h-6 text-gray-800 dark:text-white"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m14.304 4.844 2.852 2.852M7 7H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-4.5m2.409-9.91a2.017 2.017 0 0 1 0 2.853l-6.844 6.844L8 14l.713-3.565 6.844-6.844a2.015 2.015 0 0 1 2.852 0Z"
                />
              </svg>
            </a>
          </h5>
          <div className="flex justify-between items-center">
            <div>
              <p className="dark:text-gray-400 font-light text-gray-700 text-sm">
                Type: {expensesListKindLabel}
              </p>
              <p className="dark:text-gray-400 font-semibold text-gray-700 text-sm">
                Total: {totalBalance}
              </p>
            </div>
            {expensesListKind === "monthly" && (
              <div>
                <a
                  href="#"
                  className={TOP_ICON_BUTTON_CSS}
                  onClick={toggleMonthSwitcher}
                >
                  <svg
                    className="w-6 h-6"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 10h16m-8-3V4M7 7V4m10 3V4M5 20h14a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1Zm3-7h.01v.01H8V13Zm4 0h.01v.01H12V13Zm4 0h.01v.01H16V13Zm-8 4h.01v.01H8V17Zm4 0h.01v.01H12V17Zm4 0h.01v.01H16V17Z"
                    />
                  </svg>
                </a>
              </div>
            )}
          </div>
          {showMonthSwitcher && (
            <ExpensesMonthSwitcher
              monthValue={monthValue}
              setMonthValue={setMonthValue}
              expensesListKind={expensesListKind}
            />
          )}
          <hr className="h-px mt-2 bg-gray-200 border-0 dark:bg-gray-700" />
          <div className="mt-2 flex space-x-2">
            <div className="w-1/4 text-center">
              <a
                href={editExpectedExpensesPath || createExpectedExpensesPath}
                className={TOP_ICON_BUTTON_CSS}
              >
                <svg
                  className="w-6 h-6"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    fillRule="evenodd"
                    d="M12 2c-.791 0-1.55.314-2.11.874l-.893.893a.985.985 0 0 1-.696.288H7.04A2.984 2.984 0 0 0 4.055 7.04v1.262a.986.986 0 0 1-.288.696l-.893.893a2.984 2.984 0 0 0 0 4.22l.893.893a.985.985 0 0 1 .288.696v1.262a2.984 2.984 0 0 0 2.984 2.984h1.262c.261 0 .512.104.696.288l.893.893a2.984 2.984 0 0 0 4.22 0l.893-.893a.985.985 0 0 1 .696-.288h1.262a2.984 2.984 0 0 0 2.984-2.984V15.7c0-.261.104-.512.288-.696l.893-.893a2.984 2.984 0 0 0 0-4.22l-.893-.893a.985.985 0 0 1-.288-.696V7.04a2.984 2.984 0 0 0-2.984-2.984h-1.262a.985.985 0 0 1-.696-.288l-.893-.893A2.984 2.984 0 0 0 12 2Zm3.683 7.73a1 1 0 1 0-1.414-1.413l-4.253 4.253-1.277-1.277a1 1 0 0 0-1.415 1.414l1.985 1.984a1 1 0 0 0 1.414 0l4.96-4.96Z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
              <p className="dark:text-gray-400 font-light text-gray-700 text-xs">
                Expected <br />
                expenses
              </p>
            </div>
            <div className="w-1/4 text-center">
              <a
                href="#"
                onClick={openCloseYearChart}
                className={TOP_ICON_BUTTON_CSS}
              >
                <svg
                  className="w-6 h-6"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 4v15a1 1 0 0 0 1 1h15M8 16l2.5-5.5 3 3L17.273 7 20 9.667"
                  />
                </svg>
              </a>
              <p className="dark:text-gray-400 font-light text-gray-700 text-xs">
                Line
                <br />
                chart
              </p>
            </div>
            <div className="w-1/4 text-center">
              <a
                href="#"
                onClick={openCloseExpectedExpensesChart}
                className={TOP_ICON_BUTTON_CSS}
              >
                <svg
                  className="w-6 h-6"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    fillRule="evenodd"
                    d="M9 7V2.221a2 2 0 0 0-.5.365L4.586 6.5a2 2 0 0 0-.365.5H9Zm2 0V2h7a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9h5a2 2 0 0 0 2-2Zm-1 9a1 1 0 1 0-2 0v2a1 1 0 1 0 2 0v-2Zm2-5a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0v-6a1 1 0 0 1 1-1Zm4 4a1 1 0 1 0-2 0v3a1 1 0 1 0 2 0v-3Z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
              <p className="dark:text-gray-400 font-light text-gray-700 text-xs">
                Bar
                <br />
                chart
              </p>
            </div>
            <div className="w-1/4 text-center">
              <a
                href="#"
                onClick={openCloseMonthChart}
                className={TOP_ICON_BUTTON_CSS}
              >
                <svg
                  className="w-6 h-6"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 6.025A7.5 7.5 0 1 0 17.975 14H10V6.025Z"
                  />
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13.5 3c-.169 0-.334.014-.5.025V11h7.975c.011-.166.025-.331.025-.5A7.5 7.5 0 0 0 13.5 3Z"
                  />
                </svg>
              </a>
              <p className="dark:text-gray-400 font-light text-gray-700 text-xs">
                Pie
                <br />
                chart
              </p>
            </div>
          </div>
        </div>
      </div>
      {showMonthChart && (
        <ExpensesMonthChart
          month={monthValue}
          useMonth={useMonth}
          expensesPath={chartDataPath}
        />
      )}
      {showExpectedExpensesChart && (
        <ExpectedExpensesChart expensesPath={chartDataPath} />
      )}
      {showYearChart && (
        <ExpensesYearChart
          month={monthValue}
          useMonth={useMonth}
          expensesPath={chartDataPath}
        />
      )}
      <ExpensesList
        month={monthValue}
        useMonth={useMonth}
        currency={expensesListCurrency}
        supportedCurrencies={supportedCurrencies}
        categories={categories}
        expensesPath={expensesPath}
        onBalanceUpdate={handleBalanceUpdate}
      />
      <AiModal aiPath={aiPath} visible={showAiModal} onClose={closeAiModal} />
      <OpenAiModalButton onClick={openAiModal} />
    </>
  );
};

Expenses.propTypes = {
  expensesList: PropTypes.shape({
    name: PropTypes.string.isRequired,
    kind: PropTypes.string.isRequired,
    kindLabel: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    expensesPath: PropTypes.string.isRequired,
    aiPath: PropTypes.string.isRequired,
    totalBalance: PropTypes.string.isRequired,
    editExpensesListPath: PropTypes.string.isRequired,
    editExpectedExpensesPath: PropTypes.string,
    createExpectedExpensesPath: PropTypes.string,
    chartDataPath: PropTypes.string.isRequired,
  }).isRequired,
  categories: PropTypes.array,
  supportedCurrencies: PropTypes.array,
};

export default Expenses;
